import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import CustomModal from '../CustomModal';
import Row from '../../../components/Row';
import Column from '../../../components/Column';
import Text from '../../../components/Text';
import { styles } from '../../../styles/themes/style';
import DropdownMenu from '../../../components/dropdown/DropdownMenu';
import DropdownItem from '../../../components/dropdown/DropdownItem';
import { ICampaign } from '../../../interfaces/models/campaign';
import Button from '../../Button';
import { useAppHandlers } from '../../../services/actions/app/useAppHandlers';
import { useCampaignsRequest } from '../../../services/apis/campaigns/useCampaignsRequest';
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import { filterEnrollableCampaigns } from '../../../utils/campaignContactUtils';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
};

interface IMoveToCampaignModalProps {
  sourceCampaignId: string;
  count: number;
  /** Filled when the edit page already loaded the campaign list. */
  campaigns?: ICampaign[];
  campaignsReady?: boolean;
  leadIds?: string[];
  all?: boolean;
  status?: string;
  source?: string;
  search?: string;
  onMoved?: (result: { moved: number; alreadyEnrolled: number }) => void;
}

const MoveToCampaignModal: FunctionComponent<IMoveToCampaignModalProps> = ({
  sourceCampaignId,
  count,
  campaigns: prefetchedCampaigns = [],
  campaignsReady = false,
  leadIds,
  all,
  status,
  source,
  search,
  onMoved,
}) => {
  const { hideModal, showErrorToast } = useAppHandlers();
  const { getCampaigns, moveLeads } = useCampaignsRequest();
  const { store: { user } } = useAuthStore();

  const [fetchedCampaigns, setFetchedCampaigns] = useState<ICampaign[]>([]);
  const [fetching, setFetching] = useState(!campaignsReady);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getCampaignsRef = useRef(getCampaigns);
  getCampaignsRef.current = getCampaigns;

  // The edit page usually already has the list. Fetch here only if Move was
  // opened before that request finished.
  useEffect(() => {
    if (campaignsReady) return;
    let cancelled = false;
    (async () => {
      setFetching(true);
      const data = await getCampaignsRef.current('get-campaigns-move-modal');
      if (cancelled) return;
      if (!Array.isArray(data?.campaigns)) {
        setLoadFailed(true);
        setFetching(false);
        return;
      }
      setFetchedCampaigns(
        filterEnrollableCampaigns(data.campaigns, user?._id)
          .filter(campaign => campaign._id !== sourceCampaignId),
      );
      setFetching(false);
    })();
    return () => { cancelled = true; };
  }, [campaignsReady, user?._id, sourceCampaignId]);

  const campaigns = campaignsReady ? prefetchedCampaigns : fetchedCampaigns;
  const loading = !campaignsReady && fetching;

  const handleCampaignSelect = useCallback((campaignId: string) => {
    setSelectedCampaignId(campaignId);
  }, []);

  const handleSubmit = async () => {
    if (!selectedCampaignId) {
      showErrorToast({ message: 'Please select a campaign.' });
      return;
    }

    setSubmitting(true);
    try {
      const res = await moveLeads(sourceCampaignId, {
        targetCampaignId: selectedCampaignId,
        ...(all
          ? {
              all: true,
              ...(status && { status }),
              ...(source && { source }),
              ...(search && { search }),
            }
          : { leadIds: leadIds ?? [] }),
      });
      if (!res?.success) {
        showErrorToast({ message: res?.message || 'Could not move leads.' });
        return;
      }
      hideModal();
      onMoved?.({
        moved: res.data?.moved ?? 0,
        alreadyEnrolled: res.data?.alreadyEnrolled ?? 0,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCampaign = campaigns.find(campaign => campaign._id === selectedCampaignId);
  const selectedCampaignName = selectedCampaign?.name || 'Choose campaign';
  const triggerLabel = loading ? 'Loading campaigns…' : (selectedCampaignId ? selectedCampaignName : 'Choose campaign');
  const leadLabel = `${count} lead${count === 1 ? '' : 's'}`;

  const selectedCampaignButton = (
    <Container $hasValue={!!selectedCampaignId && !loading}>
      {triggerLabel}
    </Container>
  );

  return (
    <CustomModal
      headingText={count === 1 ? 'Move lead' : `Move ${leadLabel}`}
      descriptionText="They leave this campaign and start from the beginning in the one you pick. Messages already sent stay sent."
      closeButton
      preventClose={submitting}
      bodyPadding="0 24px 0px 24px;"
      onCancelHandler={() => {
        if (submitting) return false;
        hideModal();
      }}
    >
      <Column gap="24px">
        <Column gap="8px">
          <StyledDropdownMenu
            selectStyle
            title={selectedCampaignButton}
            show={isDropdownOpen}
            onToggle={setIsDropdownOpen}
            disabled={submitting}
          >
            {loading ? (
              <EmptyContainer>
                <Text $description $faded>Loading campaigns…</Text>
              </EmptyContainer>
            ) : campaigns.length > 0 ? (
              campaigns.map(campaign => (
                <DropdownItem
                  key={campaign._id}
                  onClickHandler={e => {
                    e.stopPropagation();
                    handleCampaignSelect(campaign._id);
                    setIsDropdownOpen(false);
                  }}
                  $disabled={false}
                >
                  <CampaignOption>
                    <Text>{campaign.name}</Text>
                    <Text $description $faded>{STATUS_LABELS[campaign.status] || campaign.status}</Text>
                  </CampaignOption>
                </DropdownItem>
              ))
            ) : (
              <EmptyContainer>
                <Text $description $faded>
                  {loadFailed
                    ? 'Could not load campaigns. Close this and try again.'
                    : 'No other campaign can take these leads. Create one first.'}
                </Text>
              </EmptyContainer>
            )}
          </StyledDropdownMenu>
        </Column>

        <Row spaceBetween style={{ flexDirection: 'row-reverse' }}>
          <Button
            onClickHandler={handleSubmit}
            disabled={!selectedCampaignId || submitting || !campaigns.length}
            $type="blue"
            isLoading={submitting}
            $width="140px"
            $customDisabled={styles.colors.primary200}
          >
            {submitting ? 'Moving…' : 'Move'}
          </Button>
        </Row>
      </Column>
    </CustomModal>
  );
};

const Container = styled.div<{ $hasValue?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  border: none;
  width: 100%;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  font-family: NunitoSans;
  font-weight: 400;
  color: ${({ $hasValue }) => $hasValue ? styles.colors.black600 : styles.colors.black300};
`;

const CampaignOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

const StyledDropdownMenu = styled(DropdownMenu)`
  border: none;
  .actions-dropdown + div {
    width: 100%;
    max-height: 350px;
    overflow: hidden !important;
  }
`;

const EmptyContainer = styled.div`
  padding: 16px 12px;
  text-align: center;
  color: ${styles.colors.black400};
`;

export default MoveToCampaignModal;
